import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'
import { useTranslation } from 'react-i18next'

const G = '#d4a843'
const BR = 'rgba(255,255,255,0.07)'
const CA = 'rgba(255,255,255,0.04)'
const TX = 'rgba(255,255,255,0.88)'
const MU = 'rgba(255,255,255,0.45)'
const EM = '#4ade80'

interface Assignment {
    id: string
    title: string
    prompt: string
    word_min: number
    word_max: number
    rubric: { criterion: string; points: number; description: string }[]
    max_score: number
    personal_context_hint: string | null
}

interface Submission {
    id: string
    created_at: string
    grading_status: string
    ai_score: number | null
    max_score: number | null
    ai_feedback: string | null
    score_breakdown: { criterion: string; awarded: number; max: number; comment: string }[] | null
    follow_up_question: string | null
    attempt_number: number
}

export default function EssayDropbox({
    classId, moduleId, className: degreeName
}: {
    classId: string
    moduleId: number
    className: string
}) {
    const { t } = useTranslation()
    const { user } = useAuth()

    const [assignment, setAssignment] = useState<Assignment | null>(null)
    const [checked, setChecked] = useState(false)
    const [open, setOpen] = useState(false)
    const [activeTab, setActiveTab] = useState<'write' | 'history'>('write')
    const [text, setText] = useState('')
    const [pasteDetected, setPaste] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [submissions, setSubmissions] = useState<Submission[]>([])
    const [polling, setPolling] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [latestSubId, setLatestSubId] = useState<string | null>(null)
    const typingStart = useRef<number | null>(null)

    // Check for an active module essay
    useEffect(() => {
        if (!classId) return
        supabase
            .from('university_assignments')
            .select('id, title, prompt, word_min, word_max, rubric, max_score, personal_context_hint')
            .eq('class_id', classId)
            .eq('module_id', moduleId)
            .is('lesson_id', null)
            .eq('assignment_type', 'essay')
            .eq('is_active', true)
            .maybeSingle()
            .then(({ data }) => {
                setAssignment(data as Assignment | null)
                setChecked(true)
            })
    }, [classId, moduleId])

    // Load submission history
    useEffect(() => {
        if (!user || !assignment || !open) return
        supabase
            .from('university_submissions')
            .select('id, created_at, grading_status, ai_score, max_score, ai_feedback, score_breakdown, follow_up_question, attempt_number')
            .eq('user_id', user.id)
            .eq('assignment_id', assignment.id)
            .order('attempt_number', { ascending: false })
            .then(({ data }) => setSubmissions((data || []) as Submission[]))
    }, [user, assignment, open, polling])

    // Poll latest submission
    useEffect(() => {
        if (!polling || !latestSubId) return
        const interval = setInterval(async () => {
            const { data } = await supabase
                .from('university_submissions')
                .select('id, created_at, grading_status, ai_score, max_score, ai_feedback, score_breakdown, follow_up_question, attempt_number')
                .eq('id', latestSubId)
                .single()
            if (data && data.grading_status !== 'pending' && data.grading_status !== 'grading') {
                if (data.grading_status === 'error') {
                    setPolling(false)
                    setError(t('university.essay.gradingFailed', 'Grading failed — please try submitting again.'))
                    return
                }
                setSubmissions(prev => [data as Submission, ...prev.filter(s => s.id !== data.id)])
                setPolling(false)
                setActiveTab('history')
            }
        }, 3000)
        return () => clearInterval(interval)
    }, [polling, latestSubId])

    if (!checked || !assignment) return null

    const words = text.trim().split(/\s+/).filter(Boolean).length
    const tooShort = words < assignment.word_min
    const tooLong = words > assignment.word_max
    const canSubmit = !tooShort && !tooLong && !submitting && !polling
    const pct = Math.min((words / assignment.word_min) * 100, 100)

    async function handleSubmit() {
        if (!canSubmit || !user || !assignment) return
        setSubmitting(true)

        const { count } = await supabase
            .from('university_submissions')
            .select('id', { count: 'exact', head: true })
            .eq('user_id', user.id)
            .eq('assignment_id', assignment.id)

        const attemptNumber = (count || 0) + 1

        const typingDuration = typingStart.current
            ? Math.round((Date.now() - typingStart.current) / 1000)
            : null

        const { data: sub } = await supabase
            .from('university_submissions')
            .insert({
                user_id: user.id,
                assignment_id: assignment.id,
                class_id: classId,
                module_id: moduleId,
                lesson_id: null,
                response_text: text.trim(),
                word_count: words,
                paste_detected: pasteDetected,
                typing_duration_seconds: typingDuration,
                attempt_number: attemptNumber,
                grading_status: 'pending',
            })
            .select('id, created_at, grading_status, ai_score, max_score, ai_feedback, score_breakdown, follow_up_question, attempt_number')
            .single()

        if (!sub) { setSubmitting(false); return }

        setSubmissions(prev => [sub as Submission, ...prev])
        setLatestSubId(sub.id)
        setPolling(true)
        setSubmitting(false)
        setText('')
        setPaste(false)

        supabase.functions.invoke('grade-essay', {
            body: { submission_id: sub.id },
        }).catch(console.error)
    }

    const bestSubmission = submissions.find(s => s.grading_status === 'graded') || null

    return (
        <div onClick={e => e.stopPropagation()}>
            {/* Collapsed trigger */}
            <motion.button
                whileHover={{ scale: 1.02 }}
                onClick={() => setOpen(!open)}
                style={{
                    width: '100%',
                    background: open ? 'rgba(212,168,67,0.1)' : CA,
                    border: `1px solid ${open ? 'rgba(212,168,67,0.35)' : BR}`,
                    borderRadius: 12,
                    padding: '10px 16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                }}
            >
                <span style={{ fontSize: 13, fontWeight: 700, color: open ? G : TX }}>
                    ✍️ {t('university.essay.title', 'Module Essay')} — {assignment.title}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {bestSubmission && (
                        <span style={{ fontSize: 11, fontWeight: 800, color: EM, background: 'rgba(74,222,128,0.1)', borderRadius: 100, padding: '2px 8px' }}>
                            {bestSubmission.ai_score}/{bestSubmission.max_score}
                        </span>
                    )}
                    <span style={{ fontSize: 12, color: MU }}>{open ? '▲' : '▼'}</span>
                </div>
            </motion.button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div style={{ border: `1px solid rgba(212,168,67,0.2)`, borderTop: 'none', borderRadius: '0 0 12px 12px', padding: '20px', background: 'rgba(212,168,67,0.02)' }}>

                            {/* Tabs */}
                            <div style={{ display: 'flex', gap: 4, marginBottom: 20 }}>
                                {(['write', 'history'] as const).map(tab => (
                                    <button key={tab} onClick={() => setActiveTab(tab)}
                                        style={{
                                            background: activeTab === tab ? G : CA,
                                            color: activeTab === tab ? '#06060c' : MU,
                                            border: 'none', borderRadius: 8, padding: '7px 16px',
                                            fontSize: 12, fontWeight: 700, cursor: 'pointer', transition: 'all 0.15s',
                                        }}>
                                        {tab === 'write'
                                            ? t('university.essay.tabWrite', 'Write')
                                            : `${t('university.essay.tabHistory', 'History')} ${submissions.length > 0 ? `(${submissions.length})` : ''}`}
                                    </button>
                                ))}
                            </div>

                            {/* WRITE TAB */}
                            {activeTab === 'write' && (
                                <div>
                                    {/* Prompt */}
                                    <div style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 10, padding: '14px', marginBottom: 16 }}>
                                        <p style={{ fontSize: 11, fontWeight: 800, color: G, letterSpacing: '1px', marginBottom: 8 }}>
                                            {t('university.essay.prompt', 'ESSAY PROMPT')}
                                        </p>
                                        <p style={{ fontSize: 13, color: TX, lineHeight: 1.65, margin: 0 }}>{assignment.prompt}</p>
                                        {assignment.personal_context_hint && (
                                            <p style={{ fontSize: 12, color: G, margin: '10px 0 0', fontStyle: 'italic' }}>
                                                💡 {assignment.personal_context_hint}
                                            </p>
                                        )}
                                    </div>

                                    {/* Rubric preview */}
                                    {assignment.rubric.length > 0 && (
                                        <details style={{ marginBottom: 16 }}>
                                            <summary style={{ fontSize: 12, color: MU, cursor: 'pointer', fontWeight: 600, listStyle: 'none' }}>
                                                📋 {t('university.essay.viewRubric', 'View rubric')} ({assignment.max_score} {t('university.essay.points', 'pts')})
                                            </summary>
                                            <div style={{ marginTop: 10 }}>
                                                {assignment.rubric.map((r, i) => (
                                                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${BR}` }}>
                                                        <div>
                                                            <p style={{ fontSize: 12, fontWeight: 700, color: TX, margin: '0 0 2px' }}>{r.criterion}</p>
                                                            <p style={{ fontSize: 11, color: MU, margin: 0 }}>{r.description}</p>
                                                        </div>
                                                        <span style={{ fontSize: 12, fontWeight: 800, color: G, flexShrink: 0, paddingLeft: 12 }}>{r.points}pts</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </details>
                                    )}

                                    {/* Integrity notice */}
                                    <div style={{ background: 'rgba(14,165,233,0.04)', border: '1px solid rgba(14,165,233,0.15)', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
                                        <p style={{ fontSize: 11, color: '#7dd3fc', margin: 0, lineHeight: 1.5 }}>
                                            🔒 {t('university.essay.integrityNotice', 'This is your own work. Personal examples and specific detail help your professor understand your thinking — and demonstrate genuine engagement.')}
                                        </p>
                                    </div>

                                    <textarea
                                        value={text}
                                        onChange={e => {
                                            if (!typingStart.current) typingStart.current = Date.now();
                                            setText(e.target.value);
                                        }}
                                        onPaste={() => { if (words > 10) setPaste(true) }}
                                        placeholder={t('university.essay.placeholder', `Write your essay here (${assignment.word_min}–${assignment.word_max} words)…`)}
                                        rows={8}
                                        style={{
                                            width: '100%',
                                            background: 'rgba(255,255,255,0.03)',
                                            border: `1.5px solid ${tooLong ? '#f87171' : BR}`,
                                            borderRadius: 12,
                                            padding: '14px 16px',
                                            color: TX,
                                            fontSize: 13,
                                            lineHeight: 1.7,
                                            resize: 'vertical',
                                            outline: 'none',
                                            fontFamily: "'Inter', sans-serif",
                                            boxSizing: 'border-box',
                                        }}
                                    />

                                    {/* Word count bar */}
                                    <div style={{ marginTop: 8, marginBottom: 16 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                                            <span style={{ fontSize: 11, color: tooLong ? '#f87171' : tooShort ? MU : EM }}>
                                                {words} {t('university.assessment.words', 'words')}
                                            </span>
                                            <span style={{ fontSize: 11, color: MU }}>{assignment.word_min}–{assignment.word_max}</span>
                                        </div>
                                        <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 100, height: 3, overflow: 'hidden' }}>
                                            <motion.div animate={{ width: `${pct}%` }}
                                                style={{ height: '100%', borderRadius: 100, background: tooLong ? '#f87171' : tooShort ? G : EM }} />
                                        </div>
                                    </div>

                                    {polling && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 14px', background: 'rgba(212,168,67,0.06)', borderRadius: 10 }}>
                                            <motion.div animate={{ rotate: 360 }} transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                                                style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid rgba(212,168,67,0.2)', borderTopColor: G, flexShrink: 0 }} />
                                            <span style={{ fontSize: 13, color: G }}>
                                                {t('university.assessment.grading', 'Your professor is reviewing your response…')}
                                            </span>
                                        </div>
                                    )}

                                    {error && (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14, padding: '10px 14px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: 10 }}>
                                            <span style={{ fontSize: 12, color: '#f87171', fontWeight: 500 }}>{error}</span>
                                            <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 14, padding: 0, flexShrink: 0 }}>✕</button>
                                        </div>
                                    )}

                                    <motion.button
                                        whileHover={{ scale: canSubmit ? 1.02 : 1 }}
                                        onClick={handleSubmit}
                                        disabled={!canSubmit}
                                        style={{
                                            width: '100%',
                                            background: canSubmit ? `linear-gradient(135deg, ${G}, #b8912e)` : 'rgba(255,255,255,0.05)',
                                            color: canSubmit ? '#06060c' : MU,
                                            border: 'none', borderRadius: 12, padding: '12px',
                                            fontSize: 13, fontWeight: 800, cursor: canSubmit ? 'pointer' : 'not-allowed',
                                        }}>
                                        {submitting
                                            ? t('university.assessment.submitting', 'Submitting…')
                                            : tooShort
                                                ? `${t('university.assessment.tooShort', 'Minimum')} ${assignment.word_min} ${t('university.assessment.words', 'words')}`
                                                : tooLong
                                                    ? `${t('university.assessment.tooLong', 'Maximum')} ${assignment.word_max} ${t('university.assessment.words', 'words')}`
                                                    : t('university.assessment.submit', 'Submit for Grading →')}
                                    </motion.button>
                                </div>
                            )}

                            {/* HISTORY TAB */}
                            {activeTab === 'history' && (
                                <div>
                                    {submissions.length === 0 ? (
                                        <p style={{ color: MU, fontSize: 13, textAlign: 'center', padding: '24px 0' }}>
                                            {t('university.essay.noSubmissions', 'No submissions yet. Write your essay above.')}
                                        </p>
                                    ) : (
                                        submissions.map(sub => (
                                            <div key={sub.id} style={{ background: CA, border: `1px solid ${BR}`, borderRadius: 12, padding: '16px', marginBottom: 12 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                                                    <span style={{ fontSize: 12, color: MU }}>
                                                        {t('university.essay.attempt', 'Attempt')} {sub.attempt_number} · {new Date(sub.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                    </span>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                        {sub.grading_status === 'graded' && (
                                                            <span style={{ fontSize: 12, fontWeight: 800, color: EM }}>
                                                                {sub.ai_score}/{sub.max_score} {t('university.essay.points', 'pts')}
                                                            </span>
                                                        )}
                                                        <span style={{
                                                            fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 100,
                                                            background: sub.grading_status === 'graded' ? 'rgba(74,222,128,0.12)' : sub.grading_status === 'pending' || sub.grading_status === 'grading' ? 'rgba(212,168,67,0.12)' : 'rgba(248,113,113,0.12)',
                                                            color: sub.grading_status === 'graded' ? EM : sub.grading_status === 'pending' || sub.grading_status === 'grading' ? G : '#f87171',
                                                        }}>
                                                            {sub.grading_status === 'pending' || sub.grading_status === 'grading'
                                                                ? t('university.essay.statusGrading', 'GRADING…')
                                                                : sub.grading_status === 'graded'
                                                                    ? t('university.essay.statusGraded', 'GRADED')
                                                                    : sub.grading_status.toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>

                                                {sub.ai_feedback && (
                                                    <p style={{ fontSize: 12, color: TX, lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap' }}>
                                                        {sub.ai_feedback}
                                                    </p>
                                                )}

                                                {sub.follow_up_question && (
                                                    <div style={{ marginTop: 12, background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: 8, padding: '10px 12px' }}>
                                                        <p style={{ fontSize: 11, fontWeight: 800, color: '#fbbf24', marginBottom: 6 }}>
                                                            📋 {t('university.assessment.followUpTitle', 'VERIFICATION QUESTION')}
                                                        </p>
                                                        <p style={{ fontSize: 12, color: TX, margin: 0 }}>{sub.follow_up_question}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}
